import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { ShieldBan } from "lucide-react";
import "@/App.css";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Tracker from "@/components/Tracker";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import OffersPage from "@/pages/OffersPage";
import PersonalLoan from "@/pages/PersonalLoan";
import Privacy from "@/pages/Privacy";
import Disclosures from "@/pages/Disclosures";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const SmoothScroll = () => {
  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
};

const ScrollToHash = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const RestrictedScreen = () => (
  <div className="min-h-screen bg-[#F6F7F3] flex flex-col items-center justify-center px-6 text-center" data-testid="restricted-screen">
    <ShieldBan className="w-12 h-12 text-[#D92D20] mb-6" strokeWidth={1.5} />
    <h1 className="font-display text-4xl md:text-5xl mb-4 text-[#0F1F1A]">Access Restricted</h1>
    <p className="text-[#5C6B62] max-w-md">
      This service isn't available from your network. If you believe this is a mistake, please reach out to our support team.
    </p>
    <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8A978D] mt-8">ERR_IP_RESTRICTED</p>
  </div>
);

const AccessGate = ({ children }) => {
  const [blocked, setBlocked] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    api
      .get("/health")
      .then(() => setBlocked(false))
      .catch((e) => {
        if (e.response?.status === 403) setBlocked(true);
      });
  }, [pathname]);
  if (blocked && !pathname.startsWith("/admin")) return <RestrictedScreen />;
  return children;
};

const Marketing = ({ children }) => (
  <div>
    <SiteNav />
    <main>{children}</main>
    <SiteFooter />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToHash />
        <Tracker />
        <AccessGate>
          <Routes>
            <Route path="/" element={<Marketing><Home /></Marketing>} />
            <Route path="/personal-loan" element={<Marketing><PersonalLoan /></Marketing>} />
            <Route path="/how-it-works" element={<Marketing><HowItWorks /></Marketing>} />
            <Route path="/credit-builder" element={
              <Marketing>
                <OffersPage
                  category="credit-builder"
                  eyebrow="Credit Builder Comparison"
                  title={<>Build credit on <span className="italic text-[#0C6B4F]">your</span> terms.</>}
                  intro="Hand-picked credit-building services that report where it matters. Compare vetted providers and choose the tool that fits your starting point."
                />
              </Marketing>
            } />
            <Route path="/identity-protection" element={
              <Marketing>
                <OffersPage
                  category="identity-protection"
                  eyebrow="Credit Health · Monitoring & Protection"
                  title={<>Guard the file that <span className="italic text-[#0C6B4F]">guards you.</span></>}
                  intro="Real-time credit monitoring and identity theft protection from providers we trust. Watch every bureau, catch every change."
                />
              </Marketing>
            } />
            <Route path="/privacy" element={<Marketing><Privacy /></Marketing>} />
            <Route path="/disclosures" element={<Marketing><Disclosures /></Marketing>} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </AccessGate>
        <Toaster theme="light" position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
